/**
 * Seasonal Patterns Service
 *
 * Provides intelligent seasonal awareness for crisis management and task scheduling.
 * Adapts recovery strategies based on the academic calendar and application deadlines.
 */

export class SeasonalPatterns {
  constructor(supabaseClient = null) {
    this.supabase = supabaseClient;
    this.patterns = null;
    this.lastFetch = null;
    this.cacheExpiry = 3600000; // 1 hour cache
  }

  /**
   * Get current seasonal phase based on date
   * @param {Date} date - Date to check (defaults to now)
   * @returns {Object} Seasonal phase information
   */
  async getCurrentPhase(date = new Date()) {
    const patterns = await this.getPatterns();
    const month = date.getMonth() + 1; // JavaScript months are 0-indexed

    // Find matching pattern for current month
    const currentPattern = patterns.find(pattern => {
      // Handle year-wrap patterns (e.g., December to January)
      if (pattern.month_start > pattern.month_end) {
        return month >= pattern.month_start || month <= pattern.month_end;
      }
      return month >= pattern.month_start && month <= pattern.month_end;
    });

    // Fallback to default if no pattern matches
    if (!currentPattern) {
      return this.getDefaultPattern(month);
    }

    return {
      ...currentPattern,
      current_date: date,
      days_into_phase: this.getDaysIntoPhase(date, currentPattern),
      days_remaining: this.getDaysRemaining(date, currentPattern)
    };
  }

  /**
   * Get all seasonal patterns from database or cache
   * @returns {Array} Array of seasonal patterns
   */
  async getPatterns() {
    // Check cache
    if (this.patterns && this.lastFetch && Date.now() - this.lastFetch < this.cacheExpiry) {
      return this.patterns;
    }

    try {
      // If no supabase client provided, use default patterns
      if (!this.supabase) {
        this.patterns = this.getDefaultPatterns();
        this.lastFetch = Date.now();
        return this.patterns;
      }

      const { data, error } = await this.supabase
        .from('seasonal_patterns')
        .select('*')
        .eq('is_active', true)
        .order('priority', { ascending: false });

      if (error) throw error;

      this.patterns = data || this.getDefaultPatterns();
      this.lastFetch = Date.now();

      return this.patterns;
    } catch (error) {
      console.error('[SeasonalPatterns] Error fetching patterns:', error);
      // Return default patterns if database fails
      return this.getDefaultPatterns();
    }
  }

  /**
   * Get default patterns (hardcoded fallback)
   */
  getDefaultPatterns() {
    return [
      {
        phase_name: 'Summer Foundation',
        month_start: 6,
        month_end: 8,
        timeline_pressure: 'low',
        optimal_focus: ['testing', 'college_research', 'essay_brainstorming'],
        message: 'You\'re ahead of schedule! Use this time to explore and plan thoughtfully.',
        crisis_threshold: 'almost_impossible',
        rescue_strategy: 'comprehensive_restart',
        priority: 100
      },
      {
        phase_name: 'August Launch',
        month_start: 8,
        month_end: 8,
        timeline_pressure: 'medium',
        optimal_focus: ['college_list_finalization', 'teacher_recommendations', 'profile_completion'],
        message: 'Perfect timing to get organized. Senior year starts soon!',
        crisis_threshold: 'high_procrastination',
        rescue_strategy: 'structured_catchup',
        priority: 150
      },
      {
        phase_name: 'September Sprint',
        month_start: 9,
        month_end: 9,
        timeline_pressure: 'high',
        optimal_focus: ['early_decision_focus', 'personal_essay', 'recommendation_followup'],
        message: 'Game time! Focus on your Early Decision school first.',
        crisis_threshold: 'normal_overwhelm',
        rescue_strategy: 'application_round_triage',
        priority: 200
      },
      {
        phase_name: 'October Crunch',
        month_start: 10,
        month_end: 10,
        timeline_pressure: 'very_high',
        optimal_focus: ['ED_EA_applications_only', 'essay_finalization', 'application_submission'],
        message: 'Down to the wire. Quality over quantity - focus on fewer schools.',
        crisis_threshold: 'expected_crisis',
        rescue_strategy: 'emergency_triage',
        priority: 250
      },
      {
        phase_name: 'November Pivot',
        month_start: 11,
        month_end: 11,
        timeline_pressure: 'critical',
        optimal_focus: ['submit_ED_EA', 'RD_list_finalization', 'scholarship_research'],
        message: 'Submit what you have for ED/EA. RD round gives you more time.',
        crisis_threshold: 'damage_control',
        rescue_strategy: 'salvage_and_pivot',
        priority: 300
      },
      {
        phase_name: 'December Recovery',
        month_start: 12,
        month_end: 12,
        timeline_pressure: 'medium',
        optimal_focus: ['regular_decision_applications', 'financial_aid', 'backup_options'],
        message: 'ED results coming soon. Use this time for solid RD applications.',
        crisis_threshold: 'RD_opportunity',
        rescue_strategy: 'RD_optimization',
        priority: 150
      },
      {
        phase_name: 'January Push',
        month_start: 1,
        month_end: 1,
        timeline_pressure: 'high',
        optimal_focus: ['RD_submission', 'scholarship_applications', 'FAFSA_completion'],
        message: 'Final push for Regular Decision deadlines.',
        crisis_threshold: 'final_sprint',
        rescue_strategy: 'deadline_focus',
        priority: 200
      },
      {
        phase_name: 'Spring Decisions',
        month_start: 3,
        month_end: 5,
        timeline_pressure: 'low',
        optimal_focus: ['decision_preparation', 'financial_planning', 'transition_planning'],
        message: 'Decisions are coming! Time to prepare for your next chapter.',
        crisis_threshold: 'celebration_mode',
        rescue_strategy: 'decision_support',
        priority: 50
      }
    ];
  }

  /**
   * Get default pattern for months not explicitly covered
   * @param {number} month - Month number (1-12)
   * @returns {Object} Default pattern
   */
  getDefaultPattern(month) {
    const monthNames = ['', 'January', 'February', 'March', 'April', 'May', 'June',
                       'July', 'August', 'September', 'October', 'November', 'December'];

    return {
      phase_name: `${monthNames[month]} Planning`,
      month_start: month,
      month_end: month,
      timeline_pressure: 'medium',
      optimal_focus: ['general_planning', 'task_management'],
      message: 'Stay focused on your goals and maintain steady progress.',
      crisis_threshold: 'standard',
      rescue_strategy: 'standard_optimization',
      priority: 100
    };
  }

  /**
   * Calculate days into current phase
   * @param {Date} date - Current date
   * @param {Object} pattern - Seasonal pattern
   * @returns {number} Days into phase
   */
  getDaysIntoPhase(date, pattern) {
    const phaseStart = new Date(date.getFullYear(), pattern.month_start - 1, 1);

    // Adjust for year-wrap patterns
    if (pattern.month_start > pattern.month_end && date.getMonth() + 1 <= pattern.month_end) {
      phaseStart.setFullYear(date.getFullYear() - 1);
    }

    const diffTime = Math.abs(date - phaseStart);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  /**
   * Calculate days remaining in current phase
   * @param {Date} date - Current date
   * @param {Object} pattern - Seasonal pattern
   * @returns {number} Days remaining
   */
  getDaysRemaining(date, pattern) {
    const phaseEnd = new Date(date.getFullYear(), pattern.month_end, 0); // Last day of end month

    // Adjust for year-wrap patterns
    if (pattern.month_start > pattern.month_end && date.getMonth() + 1 >= pattern.month_start) {
      phaseEnd.setFullYear(date.getFullYear() + 1);
    }

    const diffTime = Math.abs(phaseEnd - date);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  /**
   * Get pressure multiplier for crisis calculations
   * @param {string} timelinePressure - Pressure level
   * @returns {number} Multiplier value
   */
  getPressureMultiplier(timelinePressure) {
    const multipliers = {
      'low': 0.5,
      'medium': 1.0,
      'high': 1.5,
      'very_high': 2.0,
      'critical': 2.5
    };

    return multipliers[timelinePressure] || 1.0;
  }

  /**
   * Check if task aligns with seasonal focus
   * @param {Object} task - Task object
   * @param {Array} optimalFocus - Array of focus areas
   * @returns {boolean} Whether task aligns with seasonal focus
   */
  isSeasonallyAligned(task, optimalFocus) {
    if (!task || !optimalFocus) return true;

    // Map task categories/types to seasonal focus areas
    const alignmentMap = {
      'testing': ['SAT', 'ACT', 'test_prep', 'standardized_test'],
      'college_research': ['college_data', 'school_research', 'campus_visit'],
      'essay_brainstorming': ['essay', 'personal_statement', 'writing'],
      'college_list_finalization': ['college_selection', 'school_list'],
      'teacher_recommendations': ['recommendation', 'letter_of_rec'],
      'profile_completion': ['profile', 'activities_list', 'resume'],
      'early_decision_focus': ['ED', 'early_decision'],
      'personal_essay': ['personal_statement', 'common_app_essay'],
      'recommendation_followup': ['recommendation_followup', 'lor_followup'],
      'ED_EA_applications_only': ['ED', 'EA', 'early_action', 'early_decision'],
      'essay_finalization': ['essay_revision', 'essay_final'],
      'application_submission': ['submit', 'application_submission'],
      'submit_ED_EA': ['submit_early', 'ED_submission', 'EA_submission'],
      'RD_list_finalization': ['regular_decision_list', 'RD_schools'],
      'scholarship_research': ['scholarship', 'financial_aid'],
      'regular_decision_applications': ['RD', 'regular_decision'],
      'financial_aid': ['FAFSA', 'CSS', 'financial_aid'],
      'backup_options': ['safety_schools', 'backup_plans'],
      'RD_submission': ['RD_submit', 'regular_submit'],
      'scholarship_applications': ['scholarship_app', 'merit_aid'],
      'FAFSA_completion': ['FAFSA', 'federal_aid'],
      'decision_preparation': ['college_decision', 'choice_preparation'],
      'financial_planning': ['tuition_planning', 'financial_preparation'],
      'transition_planning': ['college_transition', 'orientation_prep']
    };

    // Check if task matches any optimal focus area
    for (const focus of optimalFocus) {
      const relatedTerms = alignmentMap[focus] || [];

      // Check task title, category, or tags
      const taskText = `${task.title || ''} ${task.category || ''} ${task.tags || ''}`.toLowerCase();

      if (relatedTerms.some(term => taskText.includes(term.toLowerCase()))) {
        return true;
      }
    }

    return false;
  }

  /**
   * Get recommended timeline buckets based on seasonal phase
   * @param {Object} seasonalPhase - Current seasonal phase
   * @param {Date} currentDate - Current date
   * @returns {Object} Timeline buckets configuration
   */
  getSeasonalBuckets(seasonalPhase, currentDate = new Date()) {
    const month = currentDate.getMonth() + 1;

    // October - Critical deadline period
    if (month === 10) {
      return {
        bucket1: {
          title: 'This Week',
          subtitle: 'Critical ED/EA Deadlines',
          color: 'red',
          urgency: 'critical',
          maxDays: 7
        },
        bucket2: {
          title: 'Next Week',
          subtitle: 'Submit What You Have',
          color: 'orange',
          urgency: 'high',
          maxDays: 14
        },
        bucket3: {
          title: 'Early November',
          subtitle: 'RD Preparation',
          color: 'yellow',
          urgency: 'medium',
          maxDays: 30
        }
      };
    }

    // September - Application season begins
    if (month === 9) {
      return {
        bucket1: {
          title: 'This Week',
          subtitle: 'ED/EA Focus',
          color: 'coral',
          urgency: 'high',
          maxDays: 7
        },
        bucket2: {
          title: 'Rest of September',
          subtitle: 'Essays & Apps',
          color: 'gold',
          urgency: 'medium',
          maxDays: 21
        },
        bucket3: {
          title: 'October',
          subtitle: 'Finishing Touches',
          color: 'teal',
          urgency: 'low',
          maxDays: 45
        }
      };
    }

    // November - Pivot to RD
    if (month === 11) {
      return {
        bucket1: {
          title: 'Immediate',
          subtitle: 'Final ED/EA Tasks',
          color: 'red',
          urgency: 'critical',
          maxDays: 3
        },
        bucket2: {
          title: 'This Month',
          subtitle: 'RD Applications',
          color: 'orange',
          urgency: 'high',
          maxDays: 30
        },
        bucket3: {
          title: 'December',
          subtitle: 'Scholarship & Aid',
          color: 'blue',
          urgency: 'medium',
          maxDays: 60
        }
      };
    }

    // Default buckets for other months
    return {
      bucket1: {
        title: 'This Week',
        subtitle: 'Priority Focus',
        color: 'coral',
        urgency: 'high',
        maxDays: 7
      },
      bucket2: {
        title: 'Next 2 Weeks',
        subtitle: 'Core Work',
        color: 'gold',
        urgency: 'medium',
        maxDays: 14
      },
      bucket3: {
        title: 'This Month',
        subtitle: 'Secondary Tasks',
        color: 'teal',
        urgency: 'low',
        maxDays: 30
      }
    };
  }

  /**
   * Get contextual message based on crisis level and season
   * @param {string} crisisLevel - Current crisis level
   * @param {Object} seasonalPhase - Current seasonal phase
   * @param {number} overdueCount - Number of overdue tasks
   * @param {number} urgentCount - Number of urgent tasks
   * @returns {Object} Contextual message object
   */
  getContextualMessage(crisisLevel, seasonalPhase, overdueCount, urgentCount) {
    const { phase_name, timeline_pressure, message } = seasonalPhase;

    // Emergency messages
    if (crisisLevel === 'emergency') {
      return {
        icon: '🚨',
        title: 'Emergency Mode',
        message: `You have ${overdueCount} overdue tasks and ${urgentCount} urgent ones. In ${phase_name}, focus only on your absolute top priorities. Everything else can wait.`,
        action: 'Apply Emergency Recovery',
        severity: 'critical'
      };
    }

    // Crisis messages
    if (crisisLevel === 'crisis') {
      return {
        icon: '⚡',
        title: 'Crisis Management',
        message: `${phase_name} timeline pressure is ${timeline_pressure}, but this is manageable. Focus on ${urgentCount} urgent tasks first, then tackle the backlog systematically.`,
        action: 'Apply Recovery Plan',
        severity: 'high'
      };
    }

    // Overwhelmed messages
    if (crisisLevel === 'overwhelmed') {
      return {
        icon: '😰',
        title: 'Feeling Overwhelmed',
        message: `${overdueCount} tasks need attention. ${message} Let's create a realistic plan to get you back on track.`,
        action: 'Create Recovery Plan',
        severity: 'medium'
      };
    }

    // Normal operation
    return {
      icon: '🎯',
      title: `${phase_name} Strategy`,
      message: message,
      action: 'Optimize Schedule',
      severity: 'low'
    };
  }
}

// Export class only - instances should be created with supabase client parameter